import { INBOX_TYPES } from 'shared/constants/inbox';

export { INBOX_TYPES };

// Synthetic "group" channel-type token: a value the backend expands into several
// channel types (here: the SMS-style channels) when an automation condition is
// evaluated — see AutomationRules::ChannelTypeGroups (keep the value in sync).
export const SMS_CHANNELS_GROUP = 'sms_channels';

// An admin deleted the inbox, but it waits for a super admin to remove it (and can still be
// restored). It keeps its old conversations, but takes no new work: pickers leave it out.
// Store records are snake_case, camelcased copies (e.g. in compose) use deleteRequestedAt.
export const isInboxDeleteRequested = inbox =>
  Boolean(inbox?.delete_requested_at || inbox?.deleteRequestedAt);

// An inbox new SMS can go out from: an SMS inbox not marked for deletion. A Twilio inbox of
// the WhatsApp medium cannot. Mirrors Inbox#can_send_sms? (keep them in sync).
const SMS_SENDING_CHANNEL_TYPES = [
  INBOX_TYPES.SMS,
  INBOX_TYPES.SMS_BRANA,
  INBOX_TYPES.DIDWW,
  INBOX_TYPES.TWILIO,
];

export const canSendSms = inbox => {
  if (!inbox || isInboxDeleteRequested(inbox)) return false;
  if (!SMS_SENDING_CHANNEL_TYPES.includes(inbox.channel_type)) return false;
  return inbox.channel_type !== INBOX_TYPES.TWILIO || inbox.medium === 'sms';
};

const INBOX_ICON_MAP_FILL = {
  [INBOX_TYPES.WEB]: 'i-ri-global-fill',
  [INBOX_TYPES.FB]: 'i-ri-messenger-fill',
  [INBOX_TYPES.TWITTER]: 'i-ri-twitter-x-fill',
  [INBOX_TYPES.WHATSAPP]: 'i-ri-whatsapp-fill',
  [INBOX_TYPES.API]: 'i-ri-cloudy-fill',
  [INBOX_TYPES.EMAIL]: 'i-ri-mail-fill',
  [INBOX_TYPES.TELEGRAM]: 'i-ri-telegram-fill',
  [INBOX_TYPES.LINE]: 'i-ri-line-fill',
  [INBOX_TYPES.INSTAGRAM]: 'i-ri-instagram-fill',
  [INBOX_TYPES.PHONE]: 'i-ri-phone-fill',
  [INBOX_TYPES.ALLEGRO]: 'i-ri-store-2-fill',
  [INBOX_TYPES.KAUFLAND]: 'i-ri-shopping-bag-3-fill',
};

const DEFAULT_ICON_FILL = 'i-ri-chat-1-fill';

const INBOX_ICON_MAP_LINE = {
  [INBOX_TYPES.WEB]: 'i-ri-global-line',
  [INBOX_TYPES.FB]: 'i-ri-messenger-line',
  [INBOX_TYPES.TWITTER]: 'i-ri-twitter-x-line',
  [INBOX_TYPES.WHATSAPP]: 'i-ri-whatsapp-line',
  [INBOX_TYPES.API]: 'i-ri-cloudy-line',
  [INBOX_TYPES.EMAIL]: 'i-ri-mail-line',
  [INBOX_TYPES.TELEGRAM]: 'i-ri-telegram-line',
  [INBOX_TYPES.LINE]: 'i-ri-line-line',
  [INBOX_TYPES.INSTAGRAM]: 'i-ri-instagram-line',
  [INBOX_TYPES.PHONE]: 'i-ri-phone-line',
  [INBOX_TYPES.ALLEGRO]: 'i-ri-store-2-line',
  [INBOX_TYPES.KAUFLAND]: 'i-ri-shopping-bag-3-line',
};

const DEFAULT_ICON_LINE = 'i-ri-chat-1-line';

export const getInboxSource = (type, phoneNumber, inbox) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return inbox.website_url || '';

    case INBOX_TYPES.PHONE:
      // The native SIP Phone channel exposes an array (`phone_numbers`) rather
      // than a single `phone_number`, so fall back to it when present.
      return (inbox?.phone_numbers || []).join(', ') || phoneNumber || '';

    case INBOX_TYPES.TWILIO:
    case INBOX_TYPES.WHATSAPP:
    case INBOX_TYPES.SMS:
    case INBOX_TYPES.SMS_BRANA:
    case INBOX_TYPES.DIDWW:
      return phoneNumber || '';

    case INBOX_TYPES.EMAIL:
      return inbox.email || '';

    case INBOX_TYPES.ALLEGRO:
      return inbox.seller_login || '';

    default:
      return '';
  }
};
export const getReadableInboxByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'livechat';

    case INBOX_TYPES.FB:
      return 'facebook';

    case INBOX_TYPES.TWITTER:
      return 'twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp') ? 'whatsapp' : 'sms';

    case INBOX_TYPES.SMS_BRANA:
    case INBOX_TYPES.DIDWW:
      return 'sms';

    case INBOX_TYPES.WHATSAPP:
      return 'whatsapp';

    case INBOX_TYPES.API:
      return 'api';

    case INBOX_TYPES.EMAIL:
      return 'email';

    case INBOX_TYPES.TELEGRAM:
      return 'telegram';

    case INBOX_TYPES.LINE:
      return 'line';

    default:
      return 'chat';
  }
};

export const getInboxClassByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'globe-desktop';

    case INBOX_TYPES.FB:
      return 'brand-facebook';

    case INBOX_TYPES.TWITTER:
      return 'brand-twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp')
        ? 'brand-whatsapp'
        : 'brand-sms';

    case INBOX_TYPES.SMS_BRANA:
    case INBOX_TYPES.DIDWW:
      return 'brand-sms';

    case INBOX_TYPES.WHATSAPP:
      return 'brand-whatsapp';

    case INBOX_TYPES.API:
      return 'cloud';

    case INBOX_TYPES.EMAIL:
      return 'mail';

    case INBOX_TYPES.PHONE:
      return 'phone';

    case INBOX_TYPES.TELEGRAM:
      return 'brand-telegram';

    case INBOX_TYPES.LINE:
      return 'brand-line';

    case INBOX_TYPES.INSTAGRAM:
      return 'brand-instagram';

    // Allegro has no brand icon in the Fluent set, and a marketplace reads better than the generic
    // chat bubble this would otherwise fall through to.
    case INBOX_TYPES.ALLEGRO:
      return 'cart';

    default:
      return 'chat';
  }
};

export const getInboxIconByType = (type, phoneNumber, variant = 'fill') => {
  const iconMap =
    variant === 'fill' ? INBOX_ICON_MAP_FILL : INBOX_ICON_MAP_LINE;
  const defaultIcon =
    variant === 'fill' ? DEFAULT_ICON_FILL : DEFAULT_ICON_LINE;

  // Special case for Twilio (whatsapp and sms)
  if (type === INBOX_TYPES.TWILIO && phoneNumber?.startsWith('whatsapp')) {
    return iconMap[INBOX_TYPES.WHATSAPP];
  }

  return iconMap[type] ?? defaultIcon;
};

export const getInboxWarningIconClass = (type, reauthorizationRequired) => {
  const allowedInboxTypes = [
    INBOX_TYPES.FB,
    INBOX_TYPES.EMAIL,
    INBOX_TYPES.ALLEGRO,
  ];
  if (allowedInboxTypes.includes(type) && reauthorizationRequired) {
    return 'warning';
  }
  return '';
};
